<template>
  <FlexLayout role="region">
    <vue-loading :active="isLoading" :is-full-page="true" />

    <div class="checkout-left">
      <BlockContainer :title="$t('checkout.subTitle.orderDetails')" headerVariant="secondary">
        <div v-if="basketTile && (basketTile.orderDate || basketTile.orderType)">
          <div v-if="customer.firstName || customer.lastName">
            <font-awesome-icon icon="user" class="order-details-icon" />
            <span class="flex-grow-1 basket-meta-left">
              {{ customer.firstName }}{{ customer.firstName && customer.lastName ? ' ' : '' }}{{ customer.lastName }}
            </span>
          </div>
          <div v-if="customer.emailAddr">
            <font-awesome-icon icon="envelope" class="order-details-icon" />
            <span class="flex-grow-1 basket-meta-left">
              {{ customer.emailAddr }}
            </span>
          </div>
          <div v-if="basketTile.orderDate">
            <font-awesome-icon icon="calendar-alt" class="order-details-icon" />
            <span class="flex-grow-1 basket-meta-left">
              {{ formatFullDate(basketTile.orderDate) }}
            </span>
          </div>
          <div v-if="orderType">
            <font-awesome-icon icon="receipt" class="order-details-icon" />
            <span class="flex-grow-1 basket-meta-left">
              {{ getOrderTypeLabel(orderType) }}
            </span>
          </div>
          <div v-if="basketTile.table && basketTile.table.code">
            <font-awesome-icon icon="utensils" class="order-details-icon" />
            <span class="flex-grow-1 basket-meta-left">
              {{ $t('checkout.label.table') }} {{ basketTile.table.code }}
            </span>
          </div>
        </div>
      </BlockContainer>

      <BlockContainer v-if="basketTile && orderType === OrderTypes.DELIVERY" header-variant="secondary" :title="$t('checkout.subTitle.deliveryAddress')">
        <div v-if="config.ACCOUNT.showWondCustomerDataOnly" v-html="formatCustomerAddress(wondCustomer)"></div>
        <div v-else>
          <template v-if="primaryCustomerAddress">
            <span v-html="primaryCustomerAddress.formattedAddressWithoutCData"></span>
          </template>
        </div>
        <template #actions>
          <Button secondary v-if="config.ADDRESS.enabled" variant="primary" @click="navigateToAddressBook">{{ $t('checkout.button.changeDeliveryAddress')}}</Button>
        </template>
      </BlockContainer>

      <BlockContainer :title="$t('checkout.subTitle.comment')" headerVariant="secondary" v-if="orderCommentEnabled">
        <Comment
          v-model="comment"
          :max-length="orderCommentMaxLength"
          icon="comment-medical"
          :showCloseButton="false"
          @comment-selected="onCommentSelected"
        />
      </BlockContainer>

      <BlockContainer :title="$t('checkout.subTitle.promotionBarcode')" headerVariant="secondary" v-if="promotionsEnabled">
        <PromotionBarcode
          v-model="promotionBarcode"
          @promotion-barcode-input="onPromotionBarcodeInput"
        />
      </BlockContainer>

      <BlockContainer :title="$t('checkout.subTitle.paymentMethods')" headerVariant="secondary">
        <PaymentMethods
          v-model="selectedPaymentMethod"
          :paymen-methods="paymentMethods"
          :existing-balance="existingBalance"
          :total-price="basketTile ? basketTile.basketTotal : 0"
          :label="$t('checkout.label.choosePaymentMethod')"
          icon="credit-card"
          @close="closePaymentMethodsModal"
          @select="onPaymentMethodSelected"
        />
      </BlockContainer>
    </div>

    <component
      v-if="selectedPaymentMethod.paymentType === 4"
      :is="paymentComponent"
      :subCode="selectedPaymentMethod.subCode"
      :callback="redirectTo"
    />
    <div class="checkout-right">
      <BlockContainer :title="$t('checkout.subTitle.orderOverview')">
        <Basket
          :basketTile="basketTile"
          :show-vat="true"
          :editable="false"
        />
        <template #paragraph>
          <i18n path="checkout.label.acceptTermsAndPrivacy" tag="p" style="white-space-collapse: break-spaces;">
            <template v-slot:terms>
              <a :href="config.COMPANY.links.terms" target="_blank">{{ $t('system.label.terms') }}</a>
            </template>
            <template v-slot:privacy>
              <a :href="config.COMPANY.links.privacy" target="_blank">{{ $t('system.label.privacy') }}</a>
            </template>
          </i18n>
        </template>
        <template v-slot:actions>
          <Button icon-left="money-bill" @click="pay" :disabled="!selectedPaymentMethod || !selectedPaymentMethod.code">
            {{ $t('checkout.button.pay') }}
          </Button>
        </template>
      </BlockContainer>
    </div>
  </FlexLayout>
</template>

<script>
import VueLoading from "vue-loading-overlay";
import {accountService} from "@/services/api/accountService";
import {useAppStore} from "@/stores/app";
import {useShopStore} from "@/stores/shop";
import Basket from "@/components/sub-components/Basket/Basket.vue";
import {paymentService} from "@/services/api/paymentService";
import PromotionBarcode from "@/components/sub-components/Checkout/PromotionBarcode.vue";
import basketMixin from "@/components/mixins/basketMixin";
import Button from "@/components/actions/Button.vue";
import {orderService} from "@/services/api/orderService";
import Comment from "@/components/sub-components/Checkout/Comment.vue";
import CreditCardPaymentComponentSaferpay from "@/components/main-components/CreditCardPaymentComponentSaferpay.vue";
import CreditCardPaymentComponentSecurepay from "@/components/main-components/CreditCardPaymentComponentSecurepay.vue";
import CreditCardPaymentComponentNovalnet from "@/components/main-components/CreditCardPaymentComponentNovalnet.vue";
import CreditCardPaymentComponentPayone from "@/components/main-components/CreditCardPaymentComponentPayone.vue";
import CreditCardPaymentComponentAdyen from "@/components/main-components/CreditCardPaymentComponentAdyen.vue";
import {getOrderTypeLabel as resolveOrderTypeLabel} from "@/utils/orderUtils";
import {formatFullDate} from "@/utils/dateUtils";
import BlockContainer from "@/components/layout/BlockContainer.vue";
import PaymentMethods from "@/components/sub-components/Checkout/PaymentMethods.vue";
import FlexLayout from "@/components/layout-components/Main/FlexLayout.vue";
import {OrderTypes, Workflow, Login} from "@/constants/constants";
import { formatCustomerAddress } from '@/utils/customerAddressUtils';

export default {
  mixins: [basketMixin],
  inject: ["setPageMetaTitle"],
  components: {
    FlexLayout,
    PaymentMethods,
    Comment,
    Button,
    PromotionBarcode,
    Basket,
    VueLoading,
    "credit-card-payment-saferpay": CreditCardPaymentComponentSaferpay,
    "credit-card-payment-securepay": CreditCardPaymentComponentSecurepay,
    "credit-card-payment-novalnet": CreditCardPaymentComponentNovalnet,
    "credit-card-payment-payone": CreditCardPaymentComponentPayone,
    "credit-card-payment-adyen": CreditCardPaymentComponentAdyen,
    BlockContainer,
  },
  setup() {
    const appStore = useAppStore();
    const shopStore = useShopStore();

    return { appStore, shopStore };
  },
  data() {
    return {
      isLoading: false,
      customer:{},
      wondCustomer:{},
      selectedPaymentMethod: {},
      paymentMethods: [],
      basketTile: null,
      promotionBarcode: '', // Use empty string for v-model
      confirmedPromotionBarcode: '', // Only set if confirmed
      comment: undefined,
      confirmedComment: null, // Only set if confirmed
      paymentComponent: null, // Set to null by default
      orderType: '', // new data field
      mealPlanId: '',
      reservationDateTime: '',
      table: {
        id: null,
        code: null
      }
    };
  },
  computed: {
    OrderTypes() {
      return OrderTypes
    },
    config() {
      return window.config;
    },
    selectedAddressId() {
      const raw = this.$route?.query?.selectedAddressId;
      if (raw === undefined || raw === null || raw === '') {
        return null;
      }
      const parsed = Number(raw);
      return Number.isNaN(parsed) ? null : parsed;
    },
    primaryCustomerAddress() {
      if (!this.customer.addresses || !this.customer.addresses.length) return null;
      if (this.selectedAddressId !== null) {
        const selected = this.customer.addresses.find((addr) => Number(addr.id) === this.selectedAddressId);
        if (selected) {
          return selected;
        }
      }
      return this.customer.addresses.find(addr => addr.isPrimary) || this.customer.addresses[0];
    },
    promotionsEnabled() {
      return this.appStore.isWorkflow(Workflow.MEAL_PLAN) ? window.config.MEAL_PLAN.checkout.promotions : window.config.ORDER.checkout.promotions;
    },
    orderCommentEnabled() {
      return this.appStore.isWorkflow(Workflow.MEAL_PLAN) ? window.config.MEAL_PLAN.checkout.comment.enabled : window.config.ORDER.checkout.comment.enabled;
    },
    orderCommentMaxLength() {
      return this.appStore.isWorkflow(Workflow.MEAL_PLAN) ? window.config.MEAL_PLAN.checkout.comment.maxLength : window.config.ORDER.checkout.comment.maxLength;
    },
    existingBalance() {
      if (!this.wondCustomer) return 0;
      return (this.wondCustomer.prepayBalanceCash || 0) + (this.wondCustomer.prepayBalanceVoucher || 0) + (this.wondCustomer.prepayBalanceBonus || 0);
    },
  },
  methods: {
    formatCustomerAddress,
    formatFullDate,
    getOrderTypeLabel(orderType) {
      return resolveOrderTypeLabel(orderType, this.$t.bind(this), this.$te?.bind(this));
    },
    navigateToAddressBook() {
      this.$navigate('AddressBook', {
        query: {
          mode: 'select',
          returnTo: this.$route.name,
        },
      });
    },
    redirectTo(status) {
      if (status >= 0) {
        this.makeToast("success", this.$t("checkout.messages.info.charge"));

        if (this.appStore.isWorkflow(Workflow.MEAL_PLAN)) {
          // First replace with shops, then push meals
          this.$navigateToMealPlanParameters(true);
          this.$nextTick(() => {
            this.$navigateToMealPlanner(false);
          });
        }
      }
    },
    closePaymentMethodsModal() {
      this.$bvModal.hide("payment-methods-modal");
    },
    onPaymentMethodSelected(method) {
      this.selectedPaymentMethod = method;
      this.closePaymentMethodsModal();
    },
    onCommentSelected(comment) {
      this.handleComment(comment);
    },
    onPromotionBarcodeInput(barcode) {
      console.log('[Checkout] onPromotionBarcodeInput called with barcode:', barcode);
      // Only update promotionBarcode if confirmed as valid in handlePromotionBarcode
      this.handlePromotionBarcode(barcode);
    },
    isPaymentMethodSelectable(method) {
      const totalPrice = this.basketTile ? this.basketTile.basketTotal : 0;
      return !(method.paymentType === 5 && this.existingBalance < totalPrice);
    },
    async fetchPaymentMethods() {
      this.isLoading = true;
      try {
        console.log('[Checkout] fetchPaymentMethods called');
        const paymentResult = await paymentService.getPaymentMethods();
        if (paymentResult && paymentResult.paymentDetails && paymentResult.paymentDetails.length > 0) {
          const paymentMethods = paymentResult.paymentDetails;
          this.paymentMethods = paymentMethods;
          this.selectedPaymentMethod = paymentMethods.find(method => this.isPaymentMethodSelectable(method)) || {};
        }
      } finally {
        this.isLoading = false;
      }
    },
    async fetchCustomerDetails() {
      this.isLoading = true;
      try {
        console.log('[Checkout] fetchCustomerDetails called');
        const result = await accountService.afterLogin();
        if (result.exception !== undefined || (result.customer.type !== Number(Login.UserType.GUEST_USER) && result.wondCustomer === undefined)) {
          this.makeToast('danger', this.$t('checkout.messages.error.loadCustomer'));
          return;
        }
        this.customer = result.customer;
        this.wondCustomer = result.wondCustomer;
        // Set mealPlanId and reservationDateTime if available
        if (result.basketTile) {
          this.mealPlanId = result.basketTile.mealPlanId || '';
          this.reservationDateTime = result.basketTile.orderDate || '';
          this.table = {
            id: result.basketTile.table ? result.basketTile.table.id || null : null,
            code: result.basketTile.table ? result.basketTile.table.code || null : null
          };
        }
      } finally {
        this.isLoading = false;
      }
    },
    async getCurrentOrder() {
      this.isLoading = true;
      try {
        const options = {
          mealPlanId: this.mealPlanId,
          reservationDateTime: this.reservationDateTime,
        };
        const orderType = this.appStore.isWorkflow(Workflow.MEAL_PLAN)
          ? OrderTypes.DINE_IN
          : this.appStore.currentOrderMenuOrderType;
        this.basketTile = await this.getBasketTile(orderType, options);

        // Ensure comment and promotionBarcode are set from basketTile if available
        if (this.basketTile) {
          this.comment = this.basketTile.comment || '';
          this.promotionBarcode = this.basketTile.promotionBarcode || '';
          if (this.basketTile.comment && this.basketTile.comment.length) {
            this.confirmedComment = this.basketTile.comment;
          }
          if (this.basketTile.promotionBarcode && this.basketTile.promotionBarcode.length) {
            this.confirmedPromotionBarcode = this.basketTile.promotionBarcode;
          }
          this.orderType = this.basketTile.orderType || '';
          this.mealPlanId = this.basketTile.mealPlanId || '';
          this.reservationDateTime = this.basketTile.orderDate || '';
          this.table = {
            id: this.basketTile.table ? this.basketTile.table.id || null : null,
            code: this.basketTile.table ? this.basketTile.table.code || null : null
          };
        }
      } finally {
        this.isLoading = false;
      }
    },
    async confirmOrder() {
      this.isLoading = true;
      try {
        let options = {
          mealPlanId: this.mealPlanId ? this.mealPlanId : undefined,
          reservationDateTime: this.reservationDateTime ? this.reservationDateTime : undefined,
          tableId: this.table.id ? this.table.id : undefined
        };
        const result = await orderService.confirm(
          this.orderType,
          this.comment,
          this.promotionBarcode,
          true,
          options
        );
        if (result.exception !== undefined) {
          return;
        }
        this.basketTile = result.basketTile;
        if (this.basketTile) {
          this.comment = result.comment;
          this.promotionBarcode = this.basketTile.promotionBarcode;
          this.mealPlanId = this.basketTile.mealPlanId || '';
          this.reservationDateTime = this.basketTile.orderDate || '';
          this.table = {
            id: this.basketTile.table ? this.basketTile.table.id || null : null,
            code: this.basketTile.table ? this.basketTile.table.code || null : null
          };
        }
      } finally {
        this.isLoading = false;
      }
    },
    async handlePromotionBarcode(barcode) {
      try {
        const options = {
          mealPlanId: this.mealPlanId,
          reservationDateTime: this.reservationDateTime,
        };
        const result = await orderService.confirm(
          this.orderType,
          this.comment,
          barcode,
          true,
          options
        );
        if (result && result.exception || result.errorCode !== 0) {
          console.log('[Checkout] handlePromotionBarcode error:', result.error);
          this.$root.$emit('promotion-barcode-error', result.error);
        } else {
          this.basketTile = result.basketTile;
          this.confirmedPromotionBarcode = barcode;
        }
      } catch (e) {
        this.$root.$emit('promotion-barcode-error', this.$t('checkout.messages.error.onPromotionBarcode'));
      }
    },
    async handleComment(comment) {
      try {
        const options = {
          mealPlanId: this.mealPlanId,
          reservationDateTime: this.reservationDateTime,
        };
        const result = await orderService.confirm(
          this.orderType,
          comment,
          this.promotionBarcode,
          true,
          options
        );
        if (result && result.exception || result.errorCode !== 0) {
          console.log('[Checkout] handleComment error:', result.error);
          this.$root.$emit('comment-error', result.error);
        } else {
          this.basketTile = result.basketTile;
          this.confirmedComment = comment;
        }
      } catch (e) {
        this.$root.$emit('comment-error', this.$t('checkout.messages.error.onComment'));
      }
    },
    async pay() {
      this.isLoading = true;
      const paymentMethod = this.selectedPaymentMethod.subCode ? `${this.selectedPaymentMethod.code}~~${this.selectedPaymentMethod.subCode}` : this.selectedPaymentMethod.code;
      const result = await paymentService.pay(paymentMethod);
      if (result.exception !== undefined || result.errorCode !== 0) {
        this.makeToast("danger", this.$t("checkout.messages.error.onPayment"));
        this.isLoading = false;
        return;
      }
      this.isLoading = false;

      this.appStore.setCurrentOrderGuid(result.guid);

      const paymentComponents = {
        Saferpay: "credit-card-payment-saferpay",
        Securepay: "credit-card-payment-securepay",
        Novalnet: "credit-card-payment-novalnet",
        PayOne: "credit-card-payment-payone",
        Adyen: "credit-card-payment-adyen"
      };

      if (this.selectedPaymentMethod.paymentType !== 4) {
        // If paymentType is not 4 and payment is successful, navigate
        this.makeToast("success", this.$t("checkout.messages.info.onPayment"));
        if (this.appStore.isWorkflow(Workflow.MEAL_PLAN)) {
          // First replace with shops, then push meals
          this.$navigateToMealPlanParameters(true);
          this.$nextTick(() => {
            this.$navigateToMealPlanner(false);
          });
        } else {
          this.$navigateToOrderMenuConfirmation(false);
        }
        return;
      }

      this.paymentComponent = paymentComponents[result.paymentMethod.split("~~")[0]] || null;
    },
  },
  async created() {
    await this.setPageMetaTitle(this.shopStore.name);

    try {
      await this.fetchCustomerDetails();
      await this.getCurrentOrder();
      await this.confirmOrder();
      await this.fetchPaymentMethods();

      const flashToast = this.appStore.consumeFlashToast();
      if (flashToast?.variant && flashToast?.message) {
        this.makeToast(flashToast.variant, flashToast.message);
      }
    } catch (e) {
      console.error('[Checkout] created hook error:', e);
      // handle error if needed
    }
  },
};
</script>

<style scoped>
.header-title h2 {
  font-size: 24px;
  color: var(--primary-color);
}

/* Customer Info List Style */
.column-right ul {
  list-style-type: none;
  padding: 0;
}

.column-right li {
  margin-bottom: 10px;
}

/* Button Style */
.column-right button {
  margin-top: 20px;
}

.basket-meta-left {
  text-align: left;
  margin-left: 15px;
}

.checkout-left {
  display: inline-grid;
  gap: inherit;
}
.checkout-right {
  position: sticky;
  top: 24px;
}
.order-details-icon {
  vertical-align: middle;
  width: 1.2em;
}
@media screen and (max-width:768px){
  .checkout-left {
    display: contents;
  }
  .checkout-right {
    display: contents;
  }
}
</style>
